import { UtilitiesService } from 'src/app/services/utilities.service';
import { LoggingService } from './logging.service';
import { ConfigurationService } from '../pages/offline/configuration.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { APIService, DomainValue, DomainValues, Field, FormDefinition } from './api.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class StateService {
  constructor(
    private readonly apiService: APIService,
    private readonly configurationService: ConfigurationService,
    private readonly logger: LoggingService,
    private readonly userService: UserService,
    private readonly utilitiesService: UtilitiesService,
  ) {}

  public fetchGlobalData(): void {
    this.fetchFormDefinitions().catch((error) => {
      this.logger.error(error);
    });
    if (this.apiService.onLine) {
      this.apiService
        .getCollection({ collection: 'domain-values', sort: 'name', limit: 500 })
        .then((res) => {
          this.logger.debug('domain-values', res);
          this.domainValues = res;
          this.configurationService.populateDomainValues(this.domainValues);
        })
        .catch((error) => {
          this.logger.error(error);
        });
    } else {
      this.configurationService
        .getDomainValues()
        .then((domainValues) => {
          this.domainValues = domainValues;
        })
        .catch((error) => {
          this.logger.error(error);
        });
    }
    this.userService.fetchUserGroups();
  }

  private async populateFormDefinitionCache(): Promise<void> {
    if (this.apiService.onLine) {
      const formDefinitions = [];
      let hasFetchedAll = false;
      let iteration = 0;
      let gotNoResult = 0;
      try {
        while (!hasFetchedAll) {
          const formDefs = await this.apiService.getCollection({
            collection: 'form-definitions',
            sort: 'order',
            filters: [],
            limit: 20,
            skip: iteration * 20,
            excludeMetadata: false,
          });
          formDefinitions.push(...formDefs);
          // Form Definitions have a post filter based on permissions, so it is possible
          // that no result be returned in that page but some more result are in the next page.
          if (formDefs.length === 0 && gotNoResult === 3) {
            hasFetchedAll = true;
          }
          if (formDefs.length === 0) {
            gotNoResult++;
          }
          iteration++;
        }
        await this.configurationService.populateFormDefinitions(formDefinitions);
        localStorage.setItem('FORM_DEFINITIONS_POPULATED', 'true');
      } catch (error) {
        this.logger.error(error, false);
      }
    }
  }

  public async fetchFormDefinitions(): Promise<void> {
    if (this.apiService.onLine) {
      if (!localStorage.getItem('FORM_DEFINITIONS_POPULATED')) {
        try {
          await this.populateFormDefinitionCache();
        } catch (error) {
          this.logger.error(error);
        }
      } else {
        this.populateFormDefinitionCache().catch((error) => {
          this.logger.error(error, false);
        });
      }
    }

    // TODO in some cases (ex. firefox incognito) indexDB is not supported so no form defs would be returned.
    this.configurationService
      .getFormDefinitions()
      .then((formDefinitions) => {
        this.formDefinitions = formDefinitions;
        this.apiService.formDefinitions = formDefinitions;
        setTimeout(() => {
          this.formDefinitionsLoaded$.next(true);
        }, 100);
      })
      .catch((error) => {
        this.logger.error(error);
        this.formDefinitionsLoaded$.next(false);
      });
  }

  public getFormDefinitionById(id: string): FormDefinition | null {
    if (!id) {
      throw new Error('id is mandatory');
    }
    for (const formDef of this.formDefinitions) {
      if (formDef._id === id) {
        return formDef;
      }
    }
    return null;
  }

  public async getDomainValue(name: string): Promise<DomainValue[]> {
    if (this.allDomainValues.get(name)?.length) {
      return this.allDomainValues.get(name) ?? [];
    }
    await this.fetchOnlineOfflineDomainValue(name);
    return this.allDomainValues.get(name) ?? [];
  }

  private async fetchDomainValue(name: string): Promise<DomainValue[]> {
    if (!this.allDomainValues.get(name)?.length) {
      this.allDomainValues.set(name, []);
      if (this.fetchingDomVal.indexOf(name) !== -1) {
        return this.allDomainValues.get(name) ?? [];
      }
      this.fetchingDomVal.push(name);
      await this.fetchOnlineOfflineDomainValue(name);
      this.fetchingDomVal = this.fetchingDomVal.filter((x) => x !== name);
    }

    if (this.allDomainValues.get(name)?.length) {
      this.domainValueSubject.next({ name, domainValues: this.allDomainValues.get(name) ?? [] });
    }

    return this.allDomainValues.get(name) ?? [];
  }

  public getDomainValueObs(name: string): Observable<{ name: string; domainValues: DomainValue[] }> {
    this.fetchDomainValue(name).catch((error) => {
      this.logger.error(error);
    });
    return this.domainValueSubject.asObservable();
  }

  private async fetchOnlineOfflineDomainValue(name: string): Promise<void> {
    if (!this.allDomainValues.get(name)) {
      this.allDomainValues.set(name, []);
    }
    if (this.apiService.onLine) {
      try {
        const result = await this.apiService.getCollection({
          collection: 'domain-value',
          sort: 'label',
          filters: [
            {
              field: 'domainValuesName',
              operator: 'eq',
              value: name,
            },
          ],
          limit: 1000,
          excludeMetadata: true,
        });
        // if id is present in domain values, use it to sort otherwise use label localized
        if (result && result.length > 0 && (result[0].id || result[0].id === 0)) {
          result.sort(this.compareID);
        } else if (this.utilitiesService.locale.locale === 'es') {
          result.sort(this.compareES);
        } else if (this.utilitiesService.locale.locale === 'en') {
          result.sort(this.compareEN);
        } else {
          result.sort(this.compareFR);
        }
        this.allDomainValues.set(name, []);
        this.allDomainValues.get(name)?.push(...result);
        this.configurationService.populateDomainValue(result);
      } catch (e) {
        this.logger.error(e);
      }
    } else {
      const result: DomainValue[] = await this.configurationService.getDomainValue(name);
      this.allDomainValues.set(name, []);
      this.allDomainValues.get(name)?.push(...result);
    }
  }

  public getDescriptiveFields(formDefinition: FormDefinition | undefined): Array<Field> {
    const allFields: Array<any> = [];
    if (formDefinition) {
      for (const group of formDefinition.groups) {
        const fields = group.fields?.filter((x) => x.isDescriptive);
        if (fields) {
          for (const field of fields) {
            (field as any).qualifiedFieldName = group.name + '_' + field.name;
          }
          for (const field of fields) {
            allFields.push({
              label: field.label,
              qualifiedFieldName: (field as any).qualifiedFieldName,
            });
          }
        }
      }
    }
    return allFields;
  }

  private compareID(a: any, b: any): number {
    if (!a.id && !b.id) {
      return 0;
    }
    if (!a.id) {
      return -1;
    }
    if (!b.id) {
      return 1;
    }
    if (a.id > b.id) {
      return 1;
    } else {
      return -1;
    }
  }

  private compareES(a: any, b: any): number {
    if (!a.label_es && !b.label_es) {
      return 0;
    }
    if (!a.label_es) {
      return -1;
    }
    if (!b.label_es) {
      return 1;
    }
    return a.label_es.localeCompare(b.label_es);
  }

  private compareFR(a: any, b: any): number {
    if (!a.label_fr && !b.label_fr) {
      return 0;
    }
    if (!a.label_fr) {
      return -1;
    }
    if (!b.label_fr) {
      return 1;
    }
    return a.label_fr.localeCompare(b.label_fr);
  }

  private compareEN(a: any, b: any): number {
    if (!a.label && !b.label) {
      return 0;
    }
    if (!a.label) {
      return -1;
    }
    if (!b.label) {
      return 1;
    }
    return a.label.localeCompare(b.label);
  }

  public formDefinitionType = '';
  private fetchingDomVal: Array<string> = [];
  private readonly allDomainValues: Map<string, Array<DomainValue>> = new Map<string, Array<DomainValue>>();
  private readonly domainValueSubject = new BehaviorSubject<{ name: string; domainValues: DomainValue[] }>({
    name: '',
    domainValues: [],
  });

  public selectedProgramCurrency = '';
  public domainValues: DomainValues[] = [];
  public formDefinitions: Array<FormDefinition> = [];
  public formDefinitionsLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
}
